import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Unsubscribe,
  Timestamp,
  startAfter,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';
import { Property, PropertyFormData, PropertySearchFilters, ListPropertiesOptions, ApiResponse, PaginationInfo, EntityStatus, PropertyStatus } from '@/types';
import { propertyFormSchema } from '@/utils/validationSchemas';
import { AuthService } from './auth';
import { UserRole, Permission } from '@/types';
import { FirestoreQueryBuilder } from '@/utils/firestoreQueryBuilder';
import { AppError, ErrorCode, handleError } from '@/utils/errorHandler';

export class PropertyService {
  private static collection = 'properties';
  private static cache = new Map<string, { data: any, timestamp: number }>();
  private static CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  // Helper to create initial analytics object
  private static createInitialAnalytics(): any {
    return {
      views: 0,
      favorites: 0,
      inquiries: 0,
      shares: 0,
      avgTimeOnPage: 0,
      conversionRate: 0,
      popularTimes: [],
      demographics: {
        ageGroups: {},
        incomeRanges: {},
        interests: {},
      },
    };
  }

  // Helper to construct property object from form data
  private static constructPropertyObject(
    data: PropertyFormData & { chantierId?: string },
    agentId: string
  ): Omit<Property, 'id'> {
    const now = Timestamp.now();
    return {
      metadata: {
        ...data.metadata,
        images: data.metadata.images || [],
        documents: data.metadata.documents || [],
      },
      features: data.features,
      location: data.location,
      analytics: this.createInitialAnalytics(),
      agentId: agentId,
      chantierId: data.chantierId,
      tags: data.tags || [],
      isFeatured: data.isFeatured || false,
      isActive: data.isActive !== undefined ? data.isActive : true,
      salesStatus: PropertyStatus.AVAILABLE,
      listingDate: Timestamp.fromDate(data.listingDate),
      expirationDate: data.expirationDate ? Timestamp.fromDate(data.expirationDate) : undefined,
      createdAt: now,
      updatedAt: now,
      status: EntityStatus.ACTIVE,
    };
  }

  // Create a new property with validation
  static async createProperty(propertyData: PropertyFormData, agentId: string): Promise<Property> {
    try {
      // Validate form data using Yup schema
      await propertyFormSchema.validate(propertyData, { abortEarly: false });

      // Check user permissions
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new AppError(ErrorCode.UNAUTHENTICATED, 'User must be authenticated to create properties');
      }

      if (!AuthService.hasPermission(currentUser, Permission.WRITE_PROPERTIES)) {
        throw new AppError(ErrorCode.PERMISSION_DENIED, 'User does not have permission to create properties');
      }

      if (currentUser.role !== UserRole.AGENT && currentUser.role !== UserRole.ADMIN) {
        throw new AppError(ErrorCode.PERMISSION_DENIED, 'Only agents and admins can create properties');
      }

      // Only admins can assign properties to other agents
      if (agentId !== currentUser.id && currentUser.role !== UserRole.ADMIN) {
        throw new AppError(ErrorCode.PERMISSION_DENIED, 'You can only create properties for yourself unless you are an admin');
      }

      const property = this.constructPropertyObject(propertyData, agentId || currentUser.id);

      // Create the document in Firestore
      const docRef = await addDoc(collection(db, this.collection), property);
      
      // Return the new property object with the generated ID
      return { ...property, id: docRef.id };
    } catch (error: any) {
      throw handleError(error, 'PropertyService.createProperty');
    }
  }

  // Get property by ID
  static async getProperty(id: string): Promise<Property | null> {
    try {
      if (!id) {
        throw new AppError(ErrorCode.INVALID_INPUT, 'Property ID is required');
      }

      const docRef = doc(db, this.collection, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return { 
          id: docSnap.id, 
          ...data,
          // Ensure analytics exists even for old records
          analytics: data.analytics || this.createInitialAnalytics()
        } as Property;
      }
      return null;
    } catch (error: any) {
      throw handleError(error, `PropertyService.getProperty(${id})`);
    }
  }

  // Get property by ID with real-time listener
  static subscribeToProperty(
    id: string,
    callback: (property: Property | null) => void,
    onError?: (error: AppError) => void
  ): Unsubscribe {
    if (!id) {
      callback(null);
      return () => {};
    }

    const docRef = doc(db, this.collection, id);
    
    return onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          callback({ 
            id: docSnap.id, 
            ...data,
            analytics: data.analytics || this.createInitialAnalytics()
          } as Property);
        } else {
          callback(null);
        }
      },
      (error) => {
        const appError = handleError(error, 'PropertyService.subscribeToProperty');
        if (onError) {
          onError(appError);
        } else {
          console.error('Error listening to property:', appError);
          callback(null);
        }
      }
    );
  }

  // Helper for in-memory filtering
  private static matchesInMemoryFilters(property: Property, filters: PropertySearchFilters): boolean {
    // Bedrooms
    if (filters.bedrooms) {
      if (property.metadata.bedrooms < filters.bedrooms.min || property.metadata.bedrooms > filters.bedrooms.max) return false;
    }
    // Bathrooms
    if (filters.bathrooms) {
      if (property.metadata.bathrooms < filters.bathrooms.min || property.metadata.bathrooms > filters.bathrooms.max) return false;
    }
    // Square Footage
    if (filters.squareFootage) {
      if (property.metadata.squareFootage < filters.squareFootage.min || property.metadata.squareFootage > filters.squareFootage.max) return false;
    }
    // Features
    if (filters.features && filters.features.length > 0) {
       for (const feature of filters.features) {
         // Check in features object first (most common)
         if ((property.features as any)[feature] === true) continue;
         
         // Check in metadata object (for petFriendly, furnished)
         if ((property.metadata as any)[feature] === true) continue;
         
         // If not found or false in both, the property doesn't match this feature
         return false;
       }
    }
    return true;
  }

  // Get all properties with pagination and filters (page-based)
  static async getProperties(
    filters: PropertySearchFilters = {},
    page: number = 1,
    pageSize: number = 12,
    sort: { field: string; direction: 'asc' | 'desc' } = { field: 'createdAt', direction: 'desc' }
  ): Promise<ApiResponse<Property[]>> {
    try {
      const constraints: any[] = [];

      // Apply filters that can be handled by Firestore (Equality)
      if (filters.propertyTypes && filters.propertyTypes.length > 0) {
        constraints.push(where('metadata.propertyType', 'in', filters.propertyTypes));
      }

      if (filters.listingTypes && filters.listingTypes.length > 0) {
        constraints.push(where('metadata.listingType', 'in', filters.listingTypes));
      }

      if (filters.location?.city) {
        constraints.push(where('location.city', '==', filters.location.city));
      }

      if (filters.location?.state) {
        constraints.push(where('location.state', '==', filters.location.state));
      }

      if (filters.location?.zipCode) {
        constraints.push(where('location.zipCode', '==', filters.location.zipCode));
      }

      if (filters.isActive !== undefined) {
        constraints.push(where('isActive', '==', filters.isActive));
      }

      if (filters.isFeatured !== undefined) {
        constraints.push(where('isFeatured', '==', filters.isFeatured));
      }

      // Filter out deleted properties
      constraints.push(where('status', '!=', EntityStatus.DELETED));

      // Determine if we can apply range filters in DB
      // Firestore Limitation: Only one field can have range/inequality filters
      // And if we sort, it must be on that same field (unless we have specific indexes, but simpler to assume not)
      
      let orderByField = (Array.isArray(sort) ? sort[0] : sort).field;
      let orderDirection = (Array.isArray(sort) ? sort[0] : sort).direction;

      // Special handling for search term - it usually requires ordering by title
      if (filters.searchTerm) {
        orderByField = 'metadata.title';
        orderDirection = 'asc';
        constraints.push(where('metadata.title', '>=', filters.searchTerm));
        constraints.push(where('metadata.title', '<=', filters.searchTerm + '\uf8ff'));
      } 
      // Handle Price Range in DB if we are sorting by price or if we are using default sort and can switch to price
      else if (filters.priceRange && (sort.field === 'metadata.price' || sort.field === 'price')) {
         constraints.push(where('metadata.price', '>=', filters.priceRange.min));
         constraints.push(where('metadata.price', '<=', filters.priceRange.max));
      }

      // Apply Sort
      constraints.push(orderBy(orderByField, orderDirection));

      // Execute Query
      // If we have in-memory filters to apply, we fetch more items to increase chance of filling the page
      const hasInMemoryFilters = !!filters.bedrooms || !!filters.bathrooms || !!filters.squareFootage || (filters.priceRange && orderByField !== 'metadata.price') || (filters.features && filters.features.length > 0);
      const fetchLimit = hasInMemoryFilters ? 100 : pageSize * page; // Fetch enough to cover pages
      
      constraints.push(limit(fetchLimit));

      const qFinal = query(collection(db, this.collection), ...constraints);
      const querySnapshot = await getDocs(qFinal);
      
      let properties = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Property[];

      // Apply In-Memory Filters
      if (hasInMemoryFilters) {
        properties = properties.filter(p => {
           // Price Range (if not applied in DB)
           if (filters.priceRange && orderByField !== 'metadata.price') {
             if (p.metadata.price < filters.priceRange.min || p.metadata.price > filters.priceRange.max) return false;
           }
           return this.matchesInMemoryFilters(p, filters);
        });
      }

      // Apply Pagination in Memory
      const totalItems = properties.length; // This is only the count of fetched items matching filters
      const totalPages = Math.ceil(totalItems / pageSize);
      
      // Slice for current page
      const startIndex = (page - 1) * pageSize;
      const paginatedProperties = properties.slice(startIndex, startIndex + pageSize);

      const pagination: PaginationInfo = {
        page,
        limit: pageSize,
        total: totalItems, // Approximate total
        totalPages,
        hasNext: startIndex + pageSize < totalItems,
        hasPrev: page > 1,
      };

      return {
        success: true,
        data: paginatedProperties,
        pagination,
      };
    } catch (error: any) {
      const appError = handleError(error, 'PropertyService.getProperties');
      return {
        success: false,
        error: appError.message,
      };
    }
  }

  // Get properties with cursor-based pagination (more efficient)
  static async getPropertiesWithCursor(
    filters: PropertySearchFilters = {},
    pageSize: number = 12,
    lastDoc?: QueryDocumentSnapshot
  ): Promise<{ properties: Property[]; lastDoc: QueryDocumentSnapshot | null; hasMore: boolean }> {
    try {
      let q = query(collection(db, this.collection));

      // Apply filters
      if (filters.propertyTypes && filters.propertyTypes.length > 0) {
        q = query(q, where('metadata.propertyType', 'in', filters.propertyTypes));
      }

      if (filters.listingTypes && filters.listingTypes.length > 0) {
        q = query(q, where('metadata.listingType', 'in', filters.listingTypes));
      }

      if (filters.priceRange) {
        q = query(q, where('metadata.price', '>=', filters.priceRange.min));
        q = query(q, where('metadata.price', '<=', filters.priceRange.max));
      }

      if (filters.bedrooms) {
        q = query(q, where('metadata.bedrooms', '>=', filters.bedrooms.min));
        q = query(q, where('metadata.bedrooms', '<=', filters.bedrooms.max));
      }

      if (filters.bathrooms) {
        q = query(q, where('metadata.bathrooms', '>=', filters.bathrooms.min));
        q = query(q, where('metadata.bathrooms', '<=', filters.bathrooms.max));
      }

      if (filters.squareFootage) {
        q = query(q, where('metadata.squareFootage', '>=', filters.squareFootage.min));
        q = query(q, where('metadata.squareFootage', '<=', filters.squareFootage.max));
      }

      if (filters.location?.city) {
        q = query(q, where('location.city', '==', filters.location.city));
      }

      if (filters.location?.state) {
        q = query(q, where('location.state', '==', filters.location.state));
      }

      if (filters.location?.zipCode) {
        q = query(q, where('location.zipCode', '==', filters.location.zipCode));
      }

      if (filters.isActive !== undefined) {
        q = query(q, where('isActive', '==', filters.isActive));
      }

      if (filters.isFeatured !== undefined) {
        q = query(q, where('isFeatured', '==', filters.isFeatured));
      }

      // Filter out deleted properties
      q = query(q, where('status', '!=', EntityStatus.DELETED));

      // Order by creation date (newest first)
      q = query(q, orderBy('createdAt', 'desc'));

      // Apply cursor if provided
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      // Limit results
      q = query(q, limit(pageSize + 1)); // Fetch one extra to check if there are more

      const querySnapshot = await getDocs(q);
      const docs = querySnapshot.docs;
      const hasMore = docs.length > pageSize;
      
      // Remove the extra doc if we fetched one more
      const properties = (hasMore ? docs.slice(0, pageSize) : docs).map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Property[];

      const lastDocument = docs.length > 0 ? (hasMore ? docs[pageSize - 1] : docs[docs.length - 1]) : null;

      return {
        properties,
        lastDoc: lastDocument || null,
        hasMore,
      };
    } catch (error: any) {
      throw handleError(error, 'PropertyService.getPropertiesWithCursor');
    }
  }

  // List properties with advanced filtering, sorting, pagination and caching
  static async listProperties(
    options: ListPropertiesOptions
  ): Promise<ApiResponse<Property[]>> {
    const { filters = {}, sort, pagination } = options;
    const pageSize = pagination?.limit || 12;
    const lastDoc = pagination?.cursor;

    // Generate cache key (excluding lastDoc object, using ID instead)
    const cacheKey = JSON.stringify({ 
      filters, 
      sort,
      pageSize, 
      lastDocId: lastDoc?.id 
    });
    
    // Check cache
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data as ApiResponse<Property[]>;
    }

    try {
      const collectionRef = collection(db, this.collection);
      const qb = new FirestoreQueryBuilder(collectionRef);

      // 1. Base Filters (Equality)
      qb.where('metadata.propertyType', 'in', filters.propertyTypes)
        .where('metadata.listingType', 'in', filters.listingTypes)
        .where('location.city', '==', filters.location?.city)
        .where('location.state', '==', filters.location?.state)
        .where('location.zipCode', '==', filters.location?.zipCode)
        .where('isActive', '==', filters.isActive)
        .where('isFeatured', '==', filters.isFeatured);

      // Feature filters 
      // Note: filters.features is string[], but in DB features is an object with booleans.
      // We can't strictly query this dynamically without map fields or array of strings.
      // Skipping for now as per previous implementation.

      // 2. Determine Sorting and Primary Inequality Filter
      let orderByField = 'createdAt';
      let orderDirection: 'asc' | 'desc' = 'desc';

      if (sort) {
        orderByField = (Array.isArray(sort) ? sort[0] : sort).field;
        orderDirection = (Array.isArray(sort) ? sort[0] : sort).direction;
      } else if (filters.sortOrder) {
        orderDirection = filters.sortOrder;
      }

      // Default sort logic if not specified
      if (!sort) {
        if (filters.searchTerm) {
          orderByField = 'metadata.title';
          orderDirection = 'asc';
        } else if (filters.sortBy === 'price') {
          orderByField = 'metadata.price';
        } else if (filters.sortBy === 'relevance') {
           orderByField = 'createdAt'; 
        }
      }

      // Apply Range Filter based on Sort Field
      // Firestore limitation: Range filter must match sort field
      if (orderByField === 'metadata.price') {
        qb.addRangeFilter('metadata.price', filters.priceRange);
      } else if (orderByField === 'createdAt') {
         qb.addRangeFilter('createdAt', filters.dateRange ? { start: filters.dateRange.start, end: filters.dateRange.end } : undefined);
      } else if (orderByField === 'metadata.title' && filters.searchTerm) {
         qb.where('metadata.title', '>=', filters.searchTerm)
           .where('metadata.title', '<=', filters.searchTerm + '\uf8ff');
      } else if (orderByField === 'metadata.bedrooms') {
         qb.addRangeFilter('metadata.bedrooms', filters.bedrooms);
      } else if (orderByField === 'metadata.bathrooms') {
         qb.addRangeFilter('metadata.bathrooms', filters.bathrooms);
      } else if (orderByField === 'metadata.squareFootage') {
         qb.addRangeFilter('metadata.squareFootage', filters.squareFootage);
      }

      // Apply Sort
      qb.orderBy(orderByField, orderDirection);

      // 3. Status Filter
      if (filters.isActive === undefined) {
          qb.where('status', 'in', [
              EntityStatus.ACTIVE, 
              EntityStatus.INACTIVE, 
              EntityStatus.PENDING, 
              EntityStatus.ARCHIVED
          ]);
      }

      // 4. Pagination
      qb.paginate(pageSize + 1, lastDoc);

      // Execute
      const querySnapshot = await getDocs(qb.build());
      const docs = querySnapshot.docs;
      const hasMore = docs.length > pageSize;

      const properties = (hasMore ? docs.slice(0, pageSize) : docs).map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Property[];

      const nextCursor = docs.length > 0 ? (hasMore ? docs[pageSize - 1] : docs[docs.length - 1]) : null;

      // Construct Pagination Metadata
      const paginationInfo: PaginationInfo = {
        page: pagination?.page || 1,
        limit: pageSize,
        total: 0, // Cannot calculate easily with Firestore
        totalPages: 0, // Cannot calculate easily
        hasNext: hasMore,
        hasPrev: !!pagination?.page && pagination.page > 1, 
        nextCursor: nextCursor,
      };

      const result: ApiResponse<Property[]> = {
        success: true,
        data: properties,
        pagination: paginationInfo,
      };

      // Save to cache
      this.cache.set(cacheKey, { data: result, timestamp: Date.now() });

      return result;
    } catch (error: any) {
      throw handleError(error, 'PropertyService.listProperties');
    }
  }

  // Update property with permission validation
  static async updateProperty(id: string, updates: Partial<PropertyFormData>): Promise<Property> {
    try {
      // Check user permissions
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new AppError(ErrorCode.UNAUTHENTICATED, 'User must be authenticated to update properties');
      }

      if (!AuthService.hasPermission(currentUser, Permission.WRITE_PROPERTIES)) {
        throw new AppError(ErrorCode.PERMISSION_DENIED, 'User does not have permission to update properties');
      }

      // Get existing property to check ownership
      const property = await this.getProperty(id);
      if (!property) {
        throw new AppError(ErrorCode.NOT_FOUND, 'Property not found');
      }

      // Check if user is the property owner or admin
      if (property.agentId !== currentUser.id && currentUser.role !== UserRole.ADMIN) {
        throw new AppError(ErrorCode.PERMISSION_DENIED, 'You can only update your own properties');
      }

      // Prepare updated data for validation
      // Merge existing data with updates to ensure complete object validation
      const mergedMetadata = {
        ...property.metadata,
        ...(updates.metadata || {})
      };

      // Construct existing data object for validation, converting Timestamps to Dates
      const existingListingDate = property.listingDate instanceof Timestamp ? property.listingDate.toDate() : new Date(property.listingDate);
      const existingExpirationDate = property.expirationDate 
        ? (property.expirationDate instanceof Timestamp ? property.expirationDate.toDate() : new Date(property.expirationDate))
        : undefined;

      const existingData: PropertyFormData = {
        metadata: mergedMetadata as any,
        features: updates.features || property.features,
        location: updates.location || property.location,
        tags: updates.tags || property.tags,
        isFeatured: updates.isFeatured !== undefined ? updates.isFeatured : property.isFeatured,
        isActive: updates.isActive !== undefined ? updates.isActive : property.isActive,
        listingDate: updates.listingDate || existingListingDate,
        expirationDate: updates.expirationDate !== undefined ? updates.expirationDate : existingExpirationDate,
      };

      // Validate merged data
      if (updates.metadata || updates.features || updates.location || updates.listingDate || updates.expirationDate) {
        await propertyFormSchema.validate(existingData, { abortEarly: false });
      }

      // Handle image replacement/removal
      if (updates.metadata && updates.metadata.images) {
        const oldImages = property.metadata.images || [];
        const newImages = updates.metadata.images;
        
        const imagesToDelete = oldImages.filter(img => !newImages.includes(img));
        
        if (imagesToDelete.length > 0) {
          // Clean up deleted images asynchronously
          Promise.allSettled(imagesToDelete.map(img => this.deletePropertyImage(img)))
            .then(results => {
              results.forEach((result, index) => {
                if (result.status === 'rejected') {
                  console.warn(`Failed to delete image ${imagesToDelete[index]}:`, result.reason);
                }
              });
            });
        }
      }

      // Handle document replacement/removal
      if (updates.metadata && updates.metadata.documents) {
        const oldDocs = property.metadata.documents || [];
        const newDocs = updates.metadata.documents;
        
        const docsToDelete = oldDocs.filter(doc => !newDocs.includes(doc));
        
        if (docsToDelete.length > 0) {
             Promise.allSettled(docsToDelete.map(docUrl => 
                 this.deletePropertyDocument(docUrl)
             )).then(results => {
                 results.forEach((result, index) => {
                    if (result.status === 'rejected') {
                        console.warn(`Failed to delete document ${docsToDelete[index]}:`, result.reason);
                    }
                 });
             });
        }
      }

      const docRef = doc(db, this.collection, id);
      const updateData: any = {
        updatedAt: Timestamp.now(),
      };

      // Convert dates to Timestamps
      if (updates.listingDate) {
        updateData.listingDate = Timestamp.fromDate(updates.listingDate);
      }
      if (updates.expirationDate) {
        updateData.expirationDate = Timestamp.fromDate(updates.expirationDate);
      }

      // Add other updates
      if (updates.metadata) {
        updateData.metadata = mergedMetadata;
      }
      if (updates.features) {
        updateData.features = updates.features;
      }
      if (updates.location) {
        updateData.location = updates.location;
      }
      if (updates.tags !== undefined) {
        updateData.tags = updates.tags;
      }
      if (updates.isFeatured !== undefined) {
        updateData.isFeatured = updates.isFeatured;
      }
      if (updates.isActive !== undefined) {
        updateData.isActive = updates.isActive;
      }

      await updateDoc(docRef, updateData);

      // Return the updated property
      return {
        ...property,
        ...updateData,
        // Ensure timestamps are returned correctly if not updated
        updatedAt: updateData.updatedAt,
        listingDate: updateData.listingDate || property.listingDate,
        expirationDate: updateData.expirationDate || property.expirationDate,
        metadata: mergedMetadata as any,
        features: updates.features || property.features,
        location: updates.location || property.location,
        tags: updates.tags || property.tags,
        isFeatured: updates.isFeatured !== undefined ? updates.isFeatured : property.isFeatured,
        isActive: updates.isActive !== undefined ? updates.isActive : property.isActive,
      };
    } catch (error: any) {
      throw handleError(error, `PropertyService.updateProperty(${id})`);
    }
  }

  // Delete property with soft delete option and permission validation
  static async deleteProperty(id: string, hardDelete: boolean = false): Promise<void> {
    try {
      // Check user permissions
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new AppError(ErrorCode.UNAUTHENTICATED, 'User must be authenticated to delete properties');
      }

      if (!AuthService.hasPermission(currentUser, Permission.DELETE_PROPERTIES)) {
        throw new AppError(ErrorCode.PERMISSION_DENIED, 'User does not have permission to delete properties');
      }

      // Get existing property
      const property = await this.getProperty(id);
      if (!property) {
        throw new AppError(ErrorCode.NOT_FOUND, 'Property not found');
      }

      // Check if user is the property owner or admin
      if (property.agentId !== currentUser.id && currentUser.role !== UserRole.ADMIN) {
        throw new AppError(ErrorCode.PERMISSION_DENIED, 'You can only delete your own properties');
      }

      if (hardDelete) {
        // Hard delete: Remove document and associated images
        const docRef = doc(db, this.collection, id);
        
        // Delete associated images from Storage
        if (property.metadata.images && property.metadata.images.length > 0) {
          const imageDeletionResults = await Promise.allSettled(
            property.metadata.images.map(imageUrl => this.deletePropertyImage(imageUrl))
          );
          
          imageDeletionResults.forEach((result, index) => {
            if (result.status === 'rejected') {
              console.warn(`Failed to delete image ${property.metadata.images[index]}:`, result.reason);
            }
          });
        }

        // Delete associated documents from Storage
        if (property.metadata.documents && property.metadata.documents.length > 0) {
          const docDeletionResults = await Promise.allSettled(
            property.metadata.documents.map(docUrl => 
                this.deletePropertyDocument(docUrl)
            )
          );

          docDeletionResults.forEach((result, index) => {
             if (result.status === 'rejected') {
                 console.warn(`Failed to delete document ${property.metadata.documents[index]}:`, result.reason);
             }
          });
        }

        // Note: Related matches should be deleted by a Cloud Function (onPropertyDelete)
        // to ensure data consistency without requiring client-side processing of large collections.
        
        await deleteDoc(docRef);
      } else {
        // Soft delete: Update status to DELETED
        const docRef = doc(db, this.collection, id);
        await updateDoc(docRef, {
          status: EntityStatus.DELETED,
          isActive: false,
          updatedAt: Timestamp.now(),
          // We preserve other fields to allow for potential restoration
        });
      }
    } catch (error: any) {
      throw handleError(error, `PropertyService.deleteProperty(${id})`);
    }
  }

  // Upload property image
  static async uploadPropertyImage(
    propertyId: string,
    file: File,
    imageIndex: number
  ): Promise<string> {
    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new AppError(ErrorCode.INVALID_INPUT, 'File must be an image');
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new AppError(ErrorCode.INVALID_INPUT, 'Image size must be less than 5MB');
      }

      const fileExtension = file.name.split('.').pop() || 'jpg';
      const fileName = `image-${imageIndex}-${Date.now()}.${fileExtension}`;
      // Store in a subfolder for the property
      const storageRef = ref(storage, `property-images/${propertyId}/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error: any) {
      throw handleError(error, 'PropertyService.uploadPropertyImage');
    }
  }

  // Upload property document
  static async uploadPropertyDocument(
    propertyId: string,
    file: File,
    documentType: string
  ): Promise<string> {
    try {
      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new AppError(ErrorCode.INVALID_INPUT, 'Document size must be less than 10MB');
      }

      const fileExtension = file.name.split('.').pop();
      const fileName = `${documentType.replace(/[^a-z0-9]/gi, '_')}-${Date.now()}${fileExtension ? '.' + fileExtension : ''}`;
      // Store in a subfolder for the property
      const storageRef = ref(storage, `documents/${propertyId}/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error: any) {
      throw handleError(error, 'PropertyService.uploadPropertyDocument');
    }
  }

  // Delete property image
  static async deletePropertyImage(imageUrl: string): Promise<void> {
    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    } catch (error: any) {
      // If object doesn't exist, just ignore
      if (error.code === 'storage/object-not-found') {
        return;
      }
      throw handleError(error, 'PropertyService.deletePropertyImage');
    }
  }

  // Delete property document
  static async deletePropertyDocument(documentUrl: string): Promise<void> {
    try {
      const docRef = ref(storage, documentUrl);
      await deleteObject(docRef);
    } catch (error: any) {
      // If object doesn't exist, just ignore
      if (error.code === 'storage/object-not-found') {
        return;
      }
      throw handleError(error, 'PropertyService.deletePropertyDocument');
    }
  }

  // Get properties by agent
  static async getPropertiesByAgent(agentId: string): Promise<Property[]> {
    try {
      const q = query(
        collection(db, this.collection),
        where('agentId', '==', agentId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Property[];
    } catch (error) {
      throw handleError(error, `PropertyService.getPropertiesByAgent(${agentId})`);
    }
  }

  // Get properties by chantier
  static async getPropertiesByChantier(chantierId: string): Promise<Property[]> {
    try {
      const q = query(
        collection(db, this.collection),
        where('chantierId', '==', chantierId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Property[];
    } catch (error) {
      throw handleError(error, `PropertyService.getPropertiesByChantier(${chantierId})`);
    }
  }

  // Get featured properties
  static async getFeaturedProperties(limitCount: number = 6): Promise<Property[]> {
    try {
      const q = query(
        collection(db, this.collection),
        where('isFeatured', '==', true),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Property[];
    } catch (error) {
      throw handleError(error, 'PropertyService.getFeaturedProperties');
    }
  }

  // Search properties by text
  static async searchProperties(searchTerm: string): Promise<Property[]> {
    try {
      // Note: Firestore doesn't support full-text search natively
      // In production, you'd use Algolia, Elasticsearch, or Cloud Functions
      const q = query(
        collection(db, this.collection),
        where('metadata.title', '>=', searchTerm),
        where('metadata.title', '<=', searchTerm + '\uf8ff'),
        orderBy('metadata.title')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Property[];
    } catch (error) {
      throw handleError(error, `PropertyService.searchProperties(${searchTerm})`);
    }
  }

  // Update property analytics
  static async updatePropertyAnalytics(
    propertyId: string,
    analyticsUpdate: Partial<Property['analytics']>
  ): Promise<void> {
    try {
      const docRef = doc(db, this.collection, propertyId);
      await updateDoc(docRef, {
        'analytics': analyticsUpdate,
        updatedAt: new Date(),
      });
    } catch (error) {
      throw handleError(error, `PropertyService.updatePropertyAnalytics(${propertyId})`);
    }
  }

  // Increment property views
  static async incrementPropertyViews(propertyId: string): Promise<void> {
    try {
      const property = await this.getProperty(propertyId);
      if (property) {
        await this.updatePropertyAnalytics(propertyId, {
          views: property.analytics.views + 1,
          lastViewed: new Date() as any,
        });
      }
    } catch (error) {
      // We log but don't break the app for analytics errors
      console.error(`Failed to increment property views:`, error);
    }
  }
}
